import React from 'react'
import { Link } from 'react-router-dom'
import { MdDoubleArrow } from 'react-icons/md'
import { FaLeaf, FaBrain, FaBalanceScale } from 'react-icons/fa'
import WellnessImage from '../../assets/images/wellness/wellness.webp'
import { CompanyName } from '../../utils/Constants'
import { CONTACT_PAGE_URL, WELLNESS_PAGE_URL } from '../../utils/Routes'

function HomeWellnessSection() {
  return (
    <section className='grid gap-y-8 px-8 py-8 xl:py-24 2xl:px-32 lg:px-16'>
      {/* Heading */}
      <div className='grid gap-y-4 lg:gap-y-6 justify-center'>
        <h3 className='font-titleFont text-2xl text-center border-b-2 border-solid border-colour2 dark:border-colour3 pb-2'>
          MENTAL & PERSONAL WELLNESS
        </h3>
        <h4 className='text-center lg:text-xl italic font-serif'>with {CompanyName}</h4>
      </div>

      {/* Main: Approach + Image */}
      <div className='grid lg:grid-cols-2 gap-8 dark:text-colour2'>
        {/* Approach + Benefits + CTA */}
        <section className='grid gap-6 bg-colour1 rounded-3xl shadow-md p-6'>
          <article className='grid gap-4'>
            <h5 className='text-xl font-semibold font-titleFont'>My Approach</h5>
            <p className='text-base opacity-90'>
              Thoughtful, evidence-informed coaching that meets you where you are.
              We blend practical tools with compassionate guidance to help you
              release stress, gain clarity, and build sustainable balance in your day-to-day.
            </p>
          </article>

          {/* Benefits */}
          <ul className='grid gap-4'>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaLeaf className='text-colour5 text-xl' />
              <div className='grid'>
                <span className='font-semibold'>Stress Relief</span>
                <span className='text-sm opacity-80'>
                  Reduce overwhelm with simple, repeatable grounding practices.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBrain className='text-colour5 text-xl' />
              <div className='grid'>
                <span className='font-semibold'>Clarity</span>
                <span className='text-sm opacity-80'>
                  Cut through mental noise and focus on what actually moves you forward.
                </span>
              </div>
            </li>
            <li className='grid grid-cols-[auto,1fr] items-start gap-x-3'>
              <FaBalanceScale className='text-colour5 text-xl' />
              <div className='grid'>
                <span className='font-semibold'>Balance</span>
                <span className='text-sm opacity-80'>
                  Create rhythms that support work, relationships, and your own energy.
                </span>
              </div>
            </li>
          </ul>

          {/* CTA: Learn more about coaching */}
          <div className='grid px-0 justify-center'>
            <Link
              to={WELLNESS_PAGE_URL}
              className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
              aria-label={`Learn more about wellness coaching with ${CompanyName}`}
              rel='bookmark'
              title='Wellness coaching'
            >
              <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
                <span>LEARN MORE ABOUT COACHING</span>
                <MdDoubleArrow className='text-xl lg:text-2xl' />
              </div>
            </Link>
          </div>
        </section>

        {/* Image */}
        <section className='grid rounded-3xl overflow-hidden shadow-md'>
          <img
            src={WellnessImage}
            alt='Wellness coaching — calming scene'
            className='w-full h-full object-cover'
            loading='lazy'
            decoding='async'
          />
        </section>
      </div>

      {/* Coaching Offers */}
      <div className='grid gap-6 pt-8'>
        <h5 className='text-xl font-semibold font-titleFont text-center'>Wellness Coaching Overview</h5>

        <div className='grid lg:grid-cols-3 gap-6'>
          {/* Card 1 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>Clarity Session</h6>
              <span className='text-sm italic text-colour5 opacity-80'>Single 60-minute deep dive</span>
            </div>
            <p className='text-sm opacity-90'>
              Identify your top stressors, map immediate next steps, and leave with
              a focused micro-plan you can act on this week.
            </p>
            <span className='text-sm font-semibold'>Price on request</span>
          </article>

          {/* Card 2 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>4-Week Reset</h6>
              <span className='text-sm italic text-colour5 opacity-80'>Weekly 1:1 + action prompts</span>
            </div>
            <p className='text-sm opacity-90'>
              Build momentum with gentle habit shifts, accountability, and tools to
              calm your nervous system and reclaim focus.
            </p>
            <span className='text-sm font-semibold'>Price on request</span>
          </article>

          {/* Card 3 */}
          <article className='grid gap-4 bg-colour1 dark:text-colour2 rounded-3xl shadow-md p-6'>
            <div className='grid gap-1'>
              <h6 className='text-lg font-semibold'>8-Week Transform</h6>
              <span className='text-sm italic text-colour5 opacity-80'>Structured plan + support</span>
            </div>
            <p className='text-sm opacity-90'>
              Create lasting balance with a customized roadmap, weekly coaching,
              and simple practices that fit your real life.
            </p>
            <span className='text-sm font-semibold'>Price on request</span>
          </article>
        </div>

        {/* CTA: Email for pricing */}
        <div className='grid px-0 justify-center'>
          <Link
            to={CONTACT_PAGE_URL}// adjust route for your contact/pricing page
            className='grid text-xl md:text-sm w-full p-1 bg-colour5 text-colour1 shadow-lg font-semibold font-poppins hover:brightness-90 duration-200 h-fit'
            aria-label='Email for pricing'
            rel='bookmark'
            title='Contact'
          >
            <div className='grid grid-cols-rev items-center border-[1px] border-colour1 border-solid gap-x-8 py-1 px-4 lg:py-2 lg:px-8'>
              <span> EMAIL FOR PRICING</span>
              <MdDoubleArrow className='text-xl lg:text-2xl' />
            </div>
          </Link>
        </div>
      </div>
    </section>
  )
}

export default HomeWellnessSection
